import type {
  InvoiceSourceTypeEnum,
  PackageDepartmentEnum,
} from "@/__generated__/graphql";
import { PaymentMethodScreenModal } from "@/types/invoice";
import { create } from "zustand";

type InvoiceState = {
  invoiceId: string;
  baseAmount: number;
  paidAmount: number;
  amount: number;
  balance: number;
  selectPaymentScreenModal: boolean;
  paynowScreenModal: boolean;
  creditCardScreenModal: boolean;
  addCreditCardScreenModal: boolean;
  appliedVoucher: number;
  applyMainCredit: boolean;
  applyMassageCredit: boolean;
  appliedMainCreditAmount: number;
  appliedMassageCreditAmount: number;
  currentBalance: number;
  qrCodeUrl: string;
  showVoucherModal: boolean;
  topUpCreditPackageId: string | null;
  invoiceDepartments: PackageDepartmentEnum[];
  sourceType: InvoiceSourceTypeEnum | null;
};

export type PaymentType = "paynow" | "credit-card" | "";

export const useInvoiceState = create<{
  data: InvoiceState;
  setInvoiceId: (id: string) => void;
  setBaseAmount: (baseAmount: number) => void;
  setPaidAmount: (paidAmount: number) => void;
  setAmount: (amount: number) => void;
  setBalance: (balance: number) => void;
  setAppliedVoucher: (appliedVoucher: number) => void;
  setApplyMainCredit: (applyMainCredit: boolean) => void;
  setApplyMassageCredit: (applyMassageCredit: boolean) => void;
  setAppliedMainCreditAmount: (amount: number) => void;
  setAppliedMassageCreditAmount: (amount: number) => void;
  setCurrentBalance: (currentBalance: number) => void;
  showPaymentMethodModal: (...modalNames: PaymentMethodScreenModal[]) => void;
  setShowVoucherModal: (showVoucherModal: boolean) => void;
  setTopUpCreditPackageId: (topUpCreditPackageId: string | null) => void;
  setInvoiceSourceType: (sourceType: InvoiceSourceTypeEnum | null) => void;
  setInvoiceDepartments: (invoiceDepartments: PackageDepartmentEnum[]) => void;
}>((set, get) => ({
  data: {
    invoiceId: "",
    baseAmount: 0,
    paidAmount: 0,
    amount: 0,
    balance: 0,
    selectPaymentScreenModal: false,
    paynowScreenModal: false,
    creditCardScreenModal: false,
    currentBalance: 0,
    addCreditCardScreenModal: false,
    appliedVoucher: 0,
    applyMainCredit: false,
    applyMassageCredit: false,
    appliedMainCreditAmount: 0,
    appliedMassageCreditAmount: 0,
    qrCodeUrl: "",
    showVoucherModal: false,
    topUpCreditPackageId: null,
    invoiceDepartments: [],
    sourceType: null,
  },
  setInvoiceId: (invoiceId: string) => {
    set({ data: { ...get().data, invoiceId } });
  },
  setBaseAmount: (baseAmount: number) => {
    set({ data: { ...get().data, baseAmount } });
  },
  setPaidAmount: (paidAmount: number) => {
    set({ data: { ...get().data, paidAmount } });
  },
  setAmount: (amount: number) => {
    set({ data: { ...get().data, amount } });
  },
  setBalance: (balance: number) => {
    set({ data: { ...get().data, balance } });
  },
  setAppliedVoucher: (appliedVoucher: number) => {
    set({ data: { ...get().data, appliedVoucher } });
  },
  setApplyMainCredit: (applyMainCredit: boolean) => {
    set({ data: { ...get().data, applyMainCredit } });
  },
  setApplyMassageCredit: (applyMassageCredit: boolean) => {
    set({ data: { ...get().data, applyMassageCredit } });
  },
  setAppliedMainCreditAmount: (appliedMainCreditAmount: number) => {
    set({ data: { ...get().data, appliedMainCreditAmount } });
  },
  setAppliedMassageCreditAmount: (appliedMassageCreditAmount: number) => {
    set({ data: { ...get().data, appliedMassageCreditAmount } });
  },
  setCurrentBalance: (currentBalance: number) => {
    set({ data: { ...get().data, currentBalance } });
  },
  showPaymentMethodModal: (
    ...modalNames: (PaymentMethodScreenModal | null)[]
  ) => {
    set({
      data: {
        ...get().data,
        selectPaymentScreenModal: modalNames.includes(
          PaymentMethodScreenModal.SELECT_PAYMENT,
        ),
        paynowScreenModal: modalNames.includes(PaymentMethodScreenModal.PAYNOW),
        creditCardScreenModal: modalNames.includes(
          PaymentMethodScreenModal.CREDIT_CARD,
        ),
        addCreditCardScreenModal: modalNames.includes(
          PaymentMethodScreenModal.ADD_CREDIT_CARD,
        ),
      },
    });
  },
  setShowVoucherModal: (showVoucherModal: boolean) => {
    set({ data: { ...get().data, showVoucherModal } });
  },
  setTopUpCreditPackageId: (topUpCreditPackageId: string | null) => {
    set({ data: { ...get().data, topUpCreditPackageId } });
  },
  setInvoiceDepartments: (invoiceDepartments: PackageDepartmentEnum[]) => {
    set({ data: { ...get().data, invoiceDepartments } });
  },
  setInvoiceSourceType: (sourceType: InvoiceSourceTypeEnum | null) => {
    set({ data: { ...get().data, sourceType } });
  },
}));
